<div class="flex min-h-0 flex-1 flex-col gap-3">
  @if (unavailableReason(); as reason) {
    <div class="rounded-xl border border-dashed border-border bg-card/50 p-6 text-center text-sm text-muted-foreground">
      {{ reason }}
    </div>
  } @else if (loading() && rows().length === 0) {
    <!-- First-load big overlay (matches calls grid pattern). Avoids the AG-Grid spinner
         flashing on a still-empty container. -->
    <div class="mx-auto mt-10 flex w-full max-w-xl flex-col items-center gap-4 px-8 text-muted-foreground">
      <div class="flex w-full flex-col items-center gap-2">
        <div class="text-lg font-semibold text-foreground">Loading {{ tabLabel() }}…</div>
        <div class="text-sm">Fetching records for this call.</div>
      </div>
      <div class="h-2 w-full overflow-hidden rounded-full bg-muted">
        <div class="h-full w-1/3 animate-indeterminate rounded-full bg-primary"></div>
      </div>
    </div>
  } @else if (error(); as err) {
    <hlm-alert tone="error">
      <hlm-icon name="lucideCircleAlert" size="1rem" class="mt-0.5" />
      <div class="flex flex-1 flex-col gap-2">
        <div>
          Failed to load {{ tabLabel() }} (HTTP {{ err.status }}).
          @if (err.correlationId) {
            <span class="font-mono-feat text-sm text-muted-foreground">correlation-id: {{ err.correlationId }}</span>
          }
        </div>
        <div>
          <button hlmBtn variant="outline" size="sm" type="button" (click)="reload()">
            <hlm-icon name="lucideRefreshCw" size="0.9rem" />
            Retry
          </button>
        </div>
      </div>
    </hlm-alert>
  } @else if (rows().length === 0) {
    <!-- Empty state replaces the entire grid surface inline. Search + Download toolbar would
         be dead controls here, so we hide them and offer Reload as the one useful action. -->
    <div class="flex flex-1 flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-border bg-card/50 px-6 py-10 text-center">
      <hlm-icon name="lucideInbox" size="1.6rem" class="text-muted-foreground/70" />
      <div class="flex flex-col gap-1">
        <div class="text-sm font-medium text-foreground">No {{ tabLabel().toLowerCase() }} for this call</div>
        <div class="text-sm text-muted-foreground">Nothing was returned by the backend.</div>
      </div>
      <button hlmBtn variant="outline" size="sm" type="button" (click)="reload()">
        <hlm-icon name="lucideRefreshCw" size="0.9rem" />
        Reload
      </button>
    </div>
  } @else {
    <!-- Toolbar: search + download — only when there's data to filter or export. -->
    <div class="flex flex-wrap items-center gap-3">
      <div class="relative min-w-0 flex-1">
        <hlm-icon
          name="lucideSearch"
          size="0.9rem"
          class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
        />
        <input
          hlmInput
          type="text"
          class="w-full pl-9"
          [placeholder]="'Search ' + tabLabel() + '…'"
          [value]="quickFilter()"
          (input)="onQuickFilterInput($event)"
          [attr.aria-label]="'Filter ' + tabLabel() + ' rows'"
        />
      </div>
      <span class="font-mono-feat text-sm text-muted-foreground">{{ rows().length }} {{ rows().length === 1 ? 'row' : 'rows' }}</span>
      <button
        hlmBtn variant="outline" size="sm"
        type="button"
        (click)="onExportCsv()"
        [disabled]="loading()"
      >
        <hlm-icon name="lucideDownload" size="0.9rem" />
        Download CSV
      </button>
      <!-- Optional extra toolbar action — projected by the parent (e.g. the
           Accounting tab uses this slot for the "Open Call Explainer" button,
           EB-3805). Anchored to the right of Download CSV. -->
      <ng-content select="[toolbarExtra]" />
    </div>
    <!-- Optional caption row BELOW the toolbar — for permission hints that
         should not push toolbar buttons out of alignment (EB-3805). -->
    <ng-content select="[toolbarHint]" />

    <!-- Grid -->
    <div class="relative min-h-[340px] flex-1 overflow-hidden rounded-xl border border-border">
      <ag-grid-angular
        class="ag-theme-material absolute inset-0"
        [rowData]="rows()"
        [columnDefs]="columnDefs()"
        [defaultColDef]="defaultColDef"
        [pagination]="true"
        [paginationAutoPageSize]="true"
        [quickFilterText]="quickFilter()"
        [overlayLoadingTemplate]="loadingOverlayTemplate"
        [overlayNoRowsTemplate]="noRowsOverlayTemplate"
        [isFullWidthRow]="isFullWidthRow() ?? undefined"
        [fullWidthCellRenderer]="fullWidthCellRenderer() ?? undefined"
        [rowHeight]="rowHeight() ?? undefined"
        (gridReady)="onGridReady($event)"
        (firstDataRendered)="onFirstDataRendered($event)"
        (gridSizeChanged)="onGridSizeChanged($event)"
      />
    </div>
  }
</div>
